// ============================================================================
//  WORKSPACE ARCHITECTURE (Production Baseline v1.0)
//  ---------------------------------------------------------------------------
//  Role Template  -> defines WORKSPACE   (which sidebar/nav items appear)
//  Permission     -> defines ACTIONS     (what you can do inside a page)
//  Branch Scope   -> defines DATA        (which branch's data is visible)
//
//  The sidebar is generated ONLY from the user's workspace key (resolved by the
//  backend from the user's Role Template). There is NO role hardcoding and NO
//  permission-based visibility here — permissions only gate actions server-side.
// ============================================================================
import {
  LayoutDashboard, ShoppingCart, ClipboardCheck, Truck, PackageCheck,
  Boxes, Trash2, CheckSquare, Bell, History, Database, Users as UsersIcon,
  ScrollText, BookOpen, Receipt, Radar, FileBarChart, FileInput, Coins,
  Factory, Scale, FlaskConical, Wallet, Store, UtensilsCrossed, Sparkles,
  TrendingUp, ClipboardList, Layers, Brain, Landmark, Clock, GraduationCap,
  BarChart3, Building2, Briefcase, Radio, Moon, Sunrise, ShieldAlert, ChefHat,
  ArrowLeftRight, Settings, Megaphone, StickyNote, ShieldCheck, CalendarClock,
  MessageSquareText, CupSoda, Coffee, PieChart, SlidersHorizontal, CalendarCheck,
  HeartPulse,
} from "lucide-react";

// ---------------- Master navigation catalog (key -> route + meta) -----------
// `group: ""` renders without a section header (general items).
export const NAV = {
  // General
  dashboard:        { to: "/", label: "Dashboard", icon: LayoutDashboard, group: "" },
  central_flow_ai:  { to: "/central-flow-ai", label: "Central Flow AI", icon: Sparkles, group: "" },
  confirmation_action: { to: "/confirmation-action", label: "Confirmation Action", icon: ClipboardCheck, group: "" },
  erp_health:       { to: "/erp-health", label: "Kesehatan ERP", icon: HeartPulse, group: "" },
  control_tower:    { to: "/control-tower", label: "Control Tower", icon: Radio, group: "" },
  orders:           { to: "/orders", label: "Orders", icon: ShoppingCart, group: "" },

  // Cashier / POS
  pos:              { to: "/orders/new", label: "POS", icon: ShoppingCart, group: "" },
  order_status:     { to: "/orders", label: "Order Status", icon: ClipboardList, group: "" },
  sales_upload:     { to: "/sales", label: "Sales Upload", icon: Receipt, group: "" },
  sales_input:      { to: "/sales-input", label: "Pos Penjualan", icon: ShoppingCart, group: "" },
  sales_history:    { to: "/sales-history", label: "Sales History", icon: History, group: "" },
  petty_cash:       { to: "/petty-cash", label: "Petty Cash", icon: Wallet, group: "" },
  pos_incident:     { to: "/sales-incident", label: "Incident", icon: ShieldAlert, group: "" },

  // Central Operations
  central_night:    { to: "/central-night", label: "Central Night", icon: Moon, group: "Operasi Pusat" },
  central_morning:  { to: "/central-morning", label: "Central Morning", icon: Sunrise, group: "Operasi Pusat" },
  central_production:{ to: "/central-production", label: "Central Production", icon: Factory, group: "Operasi Pusat" },

  // Verification (QC)
  branch_purchase_review: { to: "/pembelian-cabang", label: "Branch Purchase Review", icon: Store, group: "Verifikasi" },
  delivery_verification:  { to: "/delivery", label: "Delivery Verification", icon: Truck, group: "Verifikasi" },

  // Central Supply Chain
  purchasing_workspace: { to: "/purchasing-workspace", label: "Supply Chain Control Center", icon: Layers, group: "Rantai Pasok" },
  traceability:     { to: "/traceability", label: "Supply Chain Traceability", icon: Radar, group: "Rantai Pasok" },
  invoice_intake:   { to: "/invoice-intake", label: "Invoice Intake", icon: FileInput, group: "Rantai Pasok" },
  cost_ledger:      { to: "/cost-ledger", label: "Cost Ledger", icon: Coins, group: "Rantai Pasok" },
  pembelian_central:{ to: "/pembelian-central", label: "Central Purchase", icon: ShoppingCart, group: "Rantai Pasok" },

  // Central Warehouse
  warehouse_workspace: { to: "/warehouse-workspace", label: "Central Warehouse", icon: PackageCheck, group: "Gudang Pusat" },
  inventory:        { to: "/inventory", label: "Central Inventory", icon: Boxes, group: "Gudang Pusat" },
  ledger:           { to: "/ledger", label: "Stock Ledger", icon: ScrollText, group: "Gudang Pusat" },
  c_stock_take:     { to: "/opname", label: "Stock Take", icon: ClipboardCheck, group: "Gudang Pusat" },
  expiry:           { to: "/expiry", label: "Expiry", icon: CalendarClock, group: "Gudang Pusat" },
  c_receiving:      { to: "/receiving", label: "Receiving", icon: PackageCheck, group: "Gudang Pusat" },
  transfers:        { to: "/transfers", label: "Transfer Stock", icon: ArrowLeftRight, group: "Gudang Pusat" },

  // Central Production / Preparation
  kitchen_ops_dashboard: { to: "/kitchen-dashboard", label: "Dashboard Dapur", icon: LayoutDashboard, group: "Produksi Pusat" },
  c_meja_prepare:   { to: "/meja-prepare", label: "Meja Prepare", icon: ChefHat, group: "Produksi Pusat" },
  master_persiapan: { to: "/master-persiapan", label: "Master Preparation", icon: Layers, group: "Produksi Pusat" },
  kitchen_sop:      { to: "/kitchen-sop", label: "Kitchen SOP", icon: ClipboardCheck, group: "Produksi Pusat" },
  prepare_dashboard:{ to: "/meja-prepare/dashboard", label: "Prepare Dashboard", icon: BarChart3, group: "Produksi Pusat" },
  margin_dashboard: { to: "/margin-dashboard", label: "Margin Dashboard", icon: Scale, group: "Produksi Pusat" },

  // Branch Kitchen Operations
  kitchen_ops_dashboard_branch: { to: "/kitchen-dashboard", label: "Dashboard Dapur", icon: LayoutDashboard, group: "Operasi Cabang" },
  morning_ops:      { to: "/morning-ops", label: "Morning Operations", icon: Sunrise, group: "Operasi Cabang" },
  night_ops:        { to: "/night-ops", label: "Night Operations", icon: Moon, group: "Operasi Cabang" },
  b_receiving:      { to: "/receiving", label: "Penerimaan Barang", icon: PackageCheck, group: "Operasi Cabang" },
  b_daily_prep:     { to: "/meja-prepare", label: "Persiapan Harian", icon: ChefHat, group: "Operasi Cabang" },
  my_inventory:     { to: "/my-inventory", label: "Bahan Siap Masak", icon: Boxes, group: "Operasi Cabang" },
  branch_kitchen:   { to: "/branch-kitchen", label: "Alur Operasional Dapur", icon: LayoutDashboard, group: "Operasi Cabang" },
  b_stock_take:     { to: "/opname", label: "Stok Opname", icon: ClipboardCheck, group: "Operasi Cabang" },
  closing:          { to: "/closing", label: "Tutup Dapur", icon: CheckSquare, group: "Operasi Cabang" },
  employee_meals:   { to: "/employee-meals", label: "Konsumsi Karyawan", icon: UtensilsCrossed, group: "Operasi Cabang" },
  waste:            { to: "/waste", label: "Barang Rusak (Waste)", icon: Trash2, group: "Operasi Cabang" },
  loss_incident:    { to: "/loss-incident", label: "Kerugian & Insiden", icon: ShieldAlert, group: "Operasi Cabang" },
  branch_notes:     { to: "/branch-notes", label: "Catatan Cabang", icon: StickyNote, group: "Operasi Cabang" },

  // Branch Beverage Station (Bartender) — SAME routes/pages as Kitchen, beverage-scoped
  // server-side (backend filters by station). Only beverage-SPECIFIC pages are new:
  // Coffee Machine Counter & Beverage Receiving. Nav items are gated by per-branch flags.
  bartender_dashboard: { to: "/branch-kitchen", label: "Beverage Dashboard", icon: LayoutDashboard, group: "Stasiun Minuman" },
  bev_receiving:    { to: "/beverage-receiving", label: "Beverage Receiving", icon: PackageCheck, group: "Stasiun Minuman" },
  bev_inventory:    { to: "/my-inventory", label: "Beverage Inventory", icon: CupSoda, group: "Stasiun Minuman" },
  bev_base_prep:    { to: "/meja-prepare", label: "Mother Base", icon: FlaskConical, group: "Stasiun Minuman" },
  bev_recipes:      { to: "/recipes", label: "Beverage Recipes", icon: BookOpen, group: "Stasiun Minuman" },
  bev_stock_take:   { to: "/opname", label: "Beverage Stock Take", icon: ClipboardCheck, group: "Stasiun Minuman" },
  bev_waste:        { to: "/waste", label: "Beverage Waste", icon: Trash2, group: "Stasiun Minuman" },
  coffee_counter:   { to: "/coffee-machine-counter", label: "Coffee Machine Counter", icon: Coffee, group: "Stasiun Minuman" },

  // Analytics
  ai_assistant:     { to: "/ai-assistant", label: "AI Assistant", icon: Sparkles, group: "Analitik" },
  recipe_coverage:  { to: "/recipe-coverage", label: "Recipe Coverage", icon: ClipboardList, group: "Analitik" },
  sales:            { to: "/sales", label: "Sales", icon: Receipt, group: "Analitik" },
  leakage:          { to: "/leakage", label: "Leakage & Variance", icon: Radar, group: "Analitik" },
  reports:          { to: "/reports", label: "Reports", icon: FileBarChart, group: "Analitik" },
  recipes:          { to: "/recipes", label: "Recipes & BOM", icon: BookOpen, group: "Analitik" },
  price_changes:    { to: "/price-changes", label: "Price Changes", icon: TrendingUp, group: "Analitik" },
  delivery:         { to: "/delivery", label: "Delivery", icon: Truck, group: "Analitik" },

  // Finance
  finance_dashboard:{ to: "/finance", label: "Dashboard Keuangan", icon: LayoutDashboard, group: "Finance" },
  finance_reconciliation:{ to: "/finance/reconciliation", label: "Rekonsiliasi Keuangan", icon: ArrowLeftRight, group: "Finance" },
  profit_loss:      { to: "/finance/profit-loss", label: "Profit & Loss", icon: FileBarChart, group: "Finance" },
  cash_flow:        { to: "/finance/cash-flow", label: "Cash Flow", icon: Coins, group: "Finance" },
  outlet_analyzer:  { to: "/finance/outlet-analyzer", label: "Multi-Outlet Analyzer", icon: Store, group: "Finance" },
  scenario_simulator:{ to: "/finance/scenario-simulator", label: "Scenario Simulator", icon: FlaskConical, group: "Finance" },
  ai_cfo:           { to: "/finance/ai-cfo", label: "AI CFO", icon: Brain, group: "Finance" },
  bank_import:      { to: "/finance/bank-import", label: "Bank Statement Intelligence", icon: Landmark, group: "Finance" },
  accounting:       { to: "/finance/accounting", label: "Accounting", icon: Scale, group: "Finance" },
  finance_categories:{ to: "/finance/categories", label: "Finance Categories", icon: Layers, group: "Finance" },

  // Finance Lite — Owner Business Dashboard (separate layer from Accounting).
  fl_dashboard:     { to: "/finance-lite", label: "Dashboard Bisnis", icon: PieChart, group: "Finance Lite" },
  fl_config:        { to: "/finance-lite/config", label: "Konfigurasi", icon: SlidersHorizontal, group: "Finance Lite" },
  fl_expenses:      { to: "/finance-lite/expenses", label: "Biaya Operasional", icon: Receipt, group: "Finance Lite" },
  fl_closing:       { to: "/finance-lite/closing", label: "Tutup Bulan", icon: CalendarCheck, group: "Finance Lite" },

  // HRD
  hr:               { to: "/hr", label: "Dashboard HRD", icon: Building2, group: "HRD" },
  hr_employees:     { to: "/hr/employees", label: "Master Karyawan", icon: UsersIcon, group: "HRD" },
  hr_positions:     { to: "/hr/positions", label: "Master Jabatan", icon: Briefcase, group: "HRD" },
  hr_attendance:    { to: "/hr/attendance", label: "Absensi", icon: Clock, group: "HRD" },
  hr_kpi:           { to: "/hr/kpi", label: "KPI Karyawan", icon: BarChart3, group: "HRD" },
  hr_training:      { to: "/hr/training", label: "Training Center", icon: GraduationCap, group: "HRD" },
  hr_payroll:       { to: "/hr/payroll", label: "Pratinjau Payroll", icon: Wallet, group: "HRD" },
  hr_schedule:      { to: "/hr/schedule", label: "Jadwal & Cuti", icon: CalendarClock, group: "HRD" },
  hr_announcements: { to: "/hr/announcements", label: "Pengumuman HRD", icon: Megaphone, group: "HRD" },
  hr_compliance:    { to: "/hr/compliance", label: "Kepatuhan Jobdesk", icon: ClipboardCheck, group: "HRD" },
  hr_jobdesks:      { to: "/hr/jobdesks", label: "Jobdesk", icon: ClipboardList, group: "HRD" },
  hr_kpi_templates: { to: "/hr/kpi-templates", label: "Template KPI", icon: TrendingUp, group: "HRD" },
  hr_payroll_rules: { to: "/hr/payroll-rules", label: "Aturan Payroll", icon: Coins, group: "HRD" },
  hr_sop:           { to: "/hr/sop", label: "SOP & Peraturan", icon: BookOpen, group: "HRD" },

  // Administration
  ai_ops_assistant: { to: "/ai-ops-assistant", label: "AI Operations Assistant", icon: Sparkles, group: "Administrasi" },
  approvals:        { to: "/approvals", label: "Approvals", icon: ShieldCheck, group: "Administrasi" },
  master_data:      { to: "/master-data", label: "Master Data", icon: Database, group: "Administrasi" },
  beverage_settings:{ to: "/beverage-settings", label: "Beverage Station Settings", icon: Settings, group: "Administrasi" },
  users:            { to: "/users", label: "Users & Roles", icon: UsersIcon, group: "Administrasi" },
  data_integrity:   { to: "/data-integrity", label: "Data Integrity", icon: ShieldCheck, group: "Administrasi" },
  beginning_balance:{ to: "/beginning-balance", label: "Beginning Balance", icon: Wallet, group: "Administrasi" },
  email_settings:   { to: "/settings/email", label: "Email Settings", icon: Settings, group: "Administrasi" },
  notifications:    { to: "/notifications", label: "Notifications", icon: Bell, group: "Administrasi" },
  audit:            { to: "/audit", label: "Audit Timeline", icon: History, group: "Administrasi" },
  feedback:         { to: "/feedback", label: "Feedback", icon: MessageSquareText, group: "Administrasi" },
};

// ---------------- Workspace compositions (workspace_key -> ordered nav keys) -
const EXECUTIVE = [
  "dashboard", "central_flow_ai", "confirmation_action", "erp_health", "control_tower", "orders", "sales_history",
  "central_night", "central_morning", "central_production",
  "purchasing_workspace", "traceability", "invoice_intake", "cost_ledger", "pembelian_central",
  "warehouse_workspace", "inventory", "ledger", "c_stock_take", "expiry", "c_receiving", "transfers",
  "c_meja_prepare", "master_persiapan", "kitchen_sop", "prepare_dashboard", "margin_dashboard",
  "kitchen_ops_dashboard", "kitchen_ops_dashboard_branch",
  "morning_ops", "night_ops", "branch_kitchen", "my_inventory", "closing", "employee_meals", "loss_incident", "branch_notes",
  "ai_assistant", "recipe_coverage", "sales", "leakage", "reports", "recipes", "price_changes", "delivery",
  "finance_dashboard", "finance_reconciliation", "profit_loss", "cash_flow", "outlet_analyzer", "scenario_simulator", "ai_cfo", "bank_import", "accounting", "finance_categories",
  "fl_dashboard", "fl_config", "fl_expenses", "fl_closing",
  "petty_cash",
  "hr", "hr_employees", "hr_positions", "hr_attendance", "hr_kpi", "hr_training", "hr_payroll", "hr_schedule", "hr_announcements", "hr_compliance", "hr_jobdesks", "hr_kpi_templates", "hr_payroll_rules", "hr_sop",
  "approvals", "master_data", "beverage_settings", "users", "data_integrity", "beginning_balance", "email_settings", "notifications", "audit", "feedback",
  "ai_ops_assistant",
];

export const WORKSPACES = {
  // Owner + Super Admin — full application.
  executive: EXECUTIVE,

  // Kitchen Central — manages every branch's central operations.
  central_kitchen: [
    "dashboard",
    "kitchen_ops_dashboard",
    "central_night", "central_morning", "central_production", "warehouse_workspace",
    "transfers", "c_meja_prepare", "master_persiapan", "prepare_dashboard",
    "loss_incident",
    "notifications",
  ],

  // Central Checker — Quality Control (verify / approve / reject / return only).
  central_checker: [
    "dashboard",
    "central_night", "central_morning",
    "branch_purchase_review", "delivery_verification",
    "loss_incident",
    "notifications",
  ],

  // Branch Head Chef + Kitchen Staff — IDENTICAL menus (permissions differ). Branch scope only.
  // NOTE: morning_ops / night_ops are intentionally HIDDEN here (not deleted from NAV) — they
  // render empty placeholders for branch kitchen roles. Kept available in other workspaces and
  // can be re-enabled later without touching the Permission Engine / Role Templates (frozen).
  branch_kitchen: [
    "dashboard", "central_flow_ai", "orders",
    "kitchen_ops_dashboard_branch",
    "branch_kitchen",
    "b_receiving", "b_daily_prep", "my_inventory",
    "b_stock_take", "employee_meals", "waste", "loss_incident", "branch_notes", "closing",
    "notifications",
  ],

  // Branch Manager — full branch visibility + management. Branch scope only.
  branch_manager: [
    "dashboard", "orders",
    "kitchen_ops_dashboard_branch",
    "morning_ops", "night_ops", "b_receiving", "b_daily_prep", "branch_kitchen", "my_inventory",
    "b_stock_take", "closing", "employee_meals", "waste", "loss_incident", "branch_notes",
    "sales", "sales_history", "leakage", "approvals",
    "petty_cash",
    "notifications",
  ],

  // Branch Bartender — dedicated Beverage Station. REUSES the Kitchen pages (Inventory,
  // Preparation, Recipes, Stock Take, Waste) — backend scopes every read to station=beverage.
  // Beverage-SPECIFIC pages: Beverage Receiving + Coffee Machine Counter. Branch scope only.
  // Per-branch flags (mother_base / espresso / coffee_counter) further filter this list at runtime.
  branch_bartender: [
    "bartender_dashboard", "bev_receiving", "bev_inventory", "bev_base_prep", "bev_recipes",
    "bev_stock_take", "bev_waste", "coffee_counter",
    "loss_incident", "notifications", "audit",
  ],

  // Cashier — Mini POS (sales recorder only). REUSES Master Menu + Recipe/BOM + Inventory
  // engine (Menu → BOM → inventory auto-consumption). Exactly two menus, nothing else.
  cashier: [
    "central_flow_ai", "sales_input", "sales_history", "petty_cash", "pos_incident",
  ],

  // Generic Branch Staff (legacy bridge until Phase 3 cleanup).
  branch_staff: [
    "dashboard", "orders", "b_receiving", "my_inventory", "b_stock_take",
    "waste", "loss_incident", "sales", "branch_notes", "notifications",
  ],

  // ----- Legacy operational workspaces (kept functional until Phase 3) -----
  central_purchasing: [
    "dashboard", "control_tower", "orders",
    "central_night", "central_morning", "central_production",
    "purchasing_workspace", "traceability", "invoice_intake", "cost_ledger", "pembelian_central",
    "warehouse_workspace", "inventory", "ledger", "c_stock_take", "expiry",
    "c_meja_prepare", "prepare_dashboard", "margin_dashboard",
    "pembelian_central", "delivery",
    "ai_assistant", "sales", "leakage", "reports", "recipes",
    "approvals", "master_data", "notifications", "audit",
  ],
  warehouse: [
    "dashboard", "orders",
    "central_night", "central_morning",
    "purchasing_workspace", "warehouse_workspace", "inventory", "ledger", "c_stock_take", "expiry", "c_receiving",
    "c_meja_prepare", "prepare_dashboard", "delivery",
    "leakage", "approvals", "notifications", "audit",
  ],
  hq_operations: [
    "dashboard", "control_tower", "orders",
    "central_night", "central_morning", "central_production",
    "purchasing_workspace", "warehouse_workspace", "inventory", "ledger", "c_stock_take", "expiry",
    "c_meja_prepare", "master_persiapan", "prepare_dashboard", "margin_dashboard",
    "sales", "leakage", "reports", "recipe_coverage", "ai_assistant",
    "notifications", "audit",
  ],
  finance: [
    "finance_dashboard", "finance_reconciliation", "profit_loss", "cash_flow", "outlet_analyzer",
    "scenario_simulator", "ai_cfo", "bank_import", "accounting", "finance_categories",
    "notifications",
  ],
  hr: [
    "hr", "hr_employees", "hr_positions", "hr_attendance", "hr_kpi", "hr_training",
    "hr_payroll", "hr_schedule", "hr_announcements", "hr_compliance",
    "hr_jobdesks", "hr_kpi_templates", "hr_payroll_rules", "hr_sop",
    "notifications",
  ],
};

// Build grouped, ordered nav items for a workspace key. Group order follows first
// appearance in the workspace list; items keep their declared order within a group.
// `opts.beverage` (per-branch flags) gates beverage station items at runtime.
export function buildNav(workspaceKey, opts = {}) {
  const bev = opts.beverage;
  const keys = (WORKSPACES[workspaceKey] || WORKSPACES.executive).filter((k) => {
    if (!bev) return true;
    if (k === "coffee_counter") return !!(bev.espresso_enabled && bev.coffee_counter_enabled);
    if (k === "bev_base_prep") return bev.mother_base_enabled !== false;
    return true;
  });
  const order = [];
  const byGroup = {};
  keys.forEach((k) => {
    const item = NAV[k];
    if (!item) return;
    const g = item.group || "";
    if (!(g in byGroup)) { byGroup[g] = []; order.push(g); }
    byGroup[g].push({ key: k, ...item });
  });
  return order.map((g) => ({ label: g, items: byGroup[g] }));
}
